import dayjs from "dayjs";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { classNames } from "primereact/utils";
import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import usePermissions from "../../../../common/api/usePermissions";
import { useSettings } from "../../../../common/api/useSettings";
import { Tag, TAG_SIZES, TAG_TYPES } from "../../../../common/components";
import { PermissionsGate } from "../../../../common/components/PermissionsGate";
import {
    ADMIN_ROUTES,
    COMPANY_ROUTES,
    LOCATION_ROUTES,
    PERMISSIONS,
    REDIRECT_ROUTES,
    ZENDESK_URLS,
} from "../../../../common/constants";
import {
    PRODUCT_MODULES,
    PRODUCTS,
} from "../../../../common/constants/products.constants";
import { useRedirect } from "../../../../common/hooks/useRedirect";
import { SettingProduct } from "../../../../common/models";
import { useUIStore } from "../../../../common/store/useUIStore";
import { useAnalyzeStore } from "../../../analyze/store";
import { getAnalyzeTabQuery } from "../../../analyze/utils";
import { BetterspaceWordmark } from "../BetterspaceWordmark";
import { IconClose } from "../Icons";
import {
    IconAnalyze,
    IconAnnouncements,
    IconDashboard,
    IconDevices,
    IconEnergy,
    IconInstallation,
    IconIntegration,
    IconLocations,
    IconLogs,
    IconMigration,
    IconMonitor,
    IconOccupancies,
    IconRulesets,
    IconSpaces,
    IconSystemcheck,
    IconUsers,
} from "../Icons/sidebar";
import { NavItem } from "./NavItem";
import { SidebarActionButton } from "./SidebarActionButton";

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

interface SidebarNavGate {
    permissions?: string[];
    globalPermissions?: string[];
    productsToCheck?: SettingProduct[];
    checkAnyLocation?: boolean;
}

interface SidebarNavItem {
    labelKey: string;
    icon: ReactNode;
    pathname: string;
    tagKey?: string;
    gate?: SidebarNavGate;
}

const NAV_ICON_CLASSES = "w-5.5 h-5.5 flex-none";

const LOCATION_NAV_ITEMS: SidebarNavItem[] = [
    {
        labelKey: "dashboard",
        icon: <IconDashboard className={NAV_ICON_CLASSES} />,
        pathname: LOCATION_ROUTES.DASHBOARD,
    },
    {
        labelKey: "energy",
        icon: <IconEnergy className={NAV_ICON_CLASSES} />,
        pathname: LOCATION_ROUTES.ENERGY.OVERVIEW,
        gate: {
            permissions: [PERMISSIONS.LOCATION.READ],
            productsToCheck: [
                {
                    id: PRODUCTS.BETTER_ENERGY,
                    modules: [{ id: PRODUCT_MODULES.BETTER_CONTROL }],
                },
            ],
        },
    },
    {
        labelKey: "analyze",
        icon: <IconAnalyze className={NAV_ICON_CLASSES} />,
        pathname: LOCATION_ROUTES.ANALYZE_TAB,
        tagKey: "common:new",
    },
    {
        labelKey: "monitor",
        icon: <IconMonitor className={NAV_ICON_CLASSES} />,
        pathname: LOCATION_ROUTES.MONITOR,
        gate: {
            permissions: [PERMISSIONS.ENERGY_MONITOR.READ],
            productsToCheck: [
                {
                    id: PRODUCTS.BETTER_ENERGY,
                    modules: [{ id: PRODUCT_MODULES.BETTER_MONITOR }],
                },
            ],
        },
    },
    {
        labelKey: "devices",
        icon: <IconDevices className={NAV_ICON_CLASSES} />,
        pathname: LOCATION_ROUTES.DEVICES,
        gate: { permissions: [PERMISSIONS.DEVICE.READ] },
    },
    {
        labelKey: "occupancies",
        icon: <IconOccupancies className={NAV_ICON_CLASSES} />,
        pathname: LOCATION_ROUTES.OCCUPANCIES,
        gate: { permissions: [PERMISSIONS.OCCUPANCY.READ] },
    },
    {
        labelKey: "spaces",
        icon: <IconSpaces className={NAV_ICON_CLASSES} />,
        pathname: LOCATION_ROUTES.SPACES,
        gate: { permissions: [PERMISSIONS.SPACE.READ] },
    },
    {
        labelKey: "logs",
        icon: <IconLogs className={NAV_ICON_CLASSES} />,
        pathname: LOCATION_ROUTES.LOGS,
        gate: {},
    },
];

const COMPANY_NAV_ITEMS: SidebarNavItem[] = [
    {
        labelKey: "locationSettings",
        icon: <IconLocations className={NAV_ICON_CLASSES} />,
        pathname: COMPANY_ROUTES.LOCATIONS,
        gate: { permissions: [PERMISSIONS.COMPANY.READ] },
    },
    {
        labelKey: "energySettings",
        icon: <IconEnergy className={NAV_ICON_CLASSES} />,
        pathname: COMPANY_ROUTES.ENERGY.GENERAL,
        gate: {
            permissions: [PERMISSIONS.ENERGY_SETTINGS.READ],
            checkAnyLocation: true,
            productsToCheck: [
                {
                    id: PRODUCTS.BETTER_ENERGY,
                    modules: [{ id: PRODUCT_MODULES.BETTER_CONTROL }],
                },
            ],
        },
    },
    {
        labelKey: "rulesets",
        icon: <IconRulesets className={NAV_ICON_CLASSES} />,
        pathname: COMPANY_ROUTES.ENERGY.RULESETS,
        gate: {
            permissions: [PERMISSIONS.ENERGY_SETTINGS.READ],
            checkAnyLocation: true,
            productsToCheck: [
                {
                    id: PRODUCTS.BETTER_ENERGY,
                    modules: [{ id: PRODUCT_MODULES.BETTER_CONTROL }],
                },
            ],
        },
    },
    {
        labelKey: "users",
        icon: <IconUsers className={NAV_ICON_CLASSES} />,
        pathname: COMPANY_ROUTES.USERS,
        gate: { permissions: [PERMISSIONS.USER.READ] },
    },
    {
        labelKey: "integration",
        icon: <IconIntegration className={NAV_ICON_CLASSES} />,
        pathname: COMPANY_ROUTES.INTEGRATION,
        gate: { permissions: [PERMISSIONS.INTEGRATION.READ] },
    },
];

const ADMIN_NAV_ITEMS: SidebarNavItem[] = [
    {
        labelKey: "systemcheck",
        icon: <IconSystemcheck className={NAV_ICON_CLASSES} />,
        pathname: ADMIN_ROUTES.SYSTEMCHECK,
    },
    {
        labelKey: "users",
        icon: <IconUsers className={NAV_ICON_CLASSES} />,
        pathname: ADMIN_ROUTES.USERS,
    },
    {
        labelKey: "devices",
        icon: <IconDevices className={NAV_ICON_CLASSES} />,
        pathname: ADMIN_ROUTES.DEVICES,
    },
    {
        labelKey: "migration",
        icon: <IconMigration className={NAV_ICON_CLASSES} />,
        pathname: ADMIN_ROUTES.MIGRATION,
    },
    {
        labelKey: "announcements",
        icon: <IconAnnouncements className={NAV_ICON_CLASSES} />,
        pathname: ADMIN_ROUTES.ANNOUNCEMENTS,
        gate: { globalPermissions: [PERMISSIONS.ANNOUNCEMENT.READ] },
    },
];

const Sidebar = (props: SidebarProps) => {
    const { isOpen, onClose } = props;

    const { t } = useTranslation("common");
    const { theme } = useUIStore();
    const router = useRouter();
    const { triggerZendeskRedirect } = useRedirect();
    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };

    const analyzeTabId = useAnalyzeStore((state) => state.tabId);

    const { isLoadingPermissions } = usePermissions();
    const { isLoadingSettings } = useSettings({ companyId, locationId });
    const isNavLoading = isLoadingPermissions || isLoadingSettings;

    const isAdminPage = Object.values(ADMIN_ROUTES).includes(router.pathname);
    const isCompanyView = !!companyId && !locationId;

    const handleOnClickHelp = () => {
        onClose();
        triggerZendeskRedirect(ZENDESK_URLS.HELPCENTER);
    };

    const handleOnClickInstallation = () => {
        onClose();
        router.push({ pathname: REDIRECT_ROUTES.INSTALLATION });
    };

    const navItems = locationId
        ? LOCATION_NAV_ITEMS
        : companyId
        ? COMPANY_NAV_ITEMS
        : ADMIN_NAV_ITEMS;

    const renderNavItem = (item: SidebarNavItem, index: number) => {
        const navItem = (
            <motion.div
                key={`${item.labelKey}-${companyId}-${locationId}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, delay: index * 0.04 }}
            >
                <NavItem
                    label={t(item.labelKey)}
                    icon={item.icon}
                    isCompanyView={isCompanyView}
                    onClick={onClose}
                    href={{
                        pathname: item.pathname,
                        query: {
                            ...(companyId && { companyId }),
                            ...(locationId && { locationId }),
                            ...getAnalyzeTabQuery(item.pathname, analyzeTabId),
                        },
                    }}
                >
                    {item.tagKey && (
                        <Tag
                            className="h-4.5!"
                            type={TAG_TYPES.TERTIARY}
                            size={TAG_SIZES.SM}
                            label={t(item.tagKey)}
                        />
                    )}
                </NavItem>
            </motion.div>
        );

        if (!item.gate) {
            return <Fragment key={item.labelKey}>{navItem}</Fragment>;
        }

        return (
            <PermissionsGate key={item.labelKey} {...item.gate}>
                {navItem}
            </PermissionsGate>
        );
    };

    return (
        <div className="shrink-0 md:h-full md:w-60">
            <div
                aria-hidden
                onClick={onClose}
                className={classNames(
                    "fixed inset-0 z-50 bg-text-500/55 transition-opacity duration-300 md:hidden",
                    {
                        "opacity-100": isOpen,
                        "pointer-events-none opacity-0": !isOpen,
                    }
                )}
            />
            <div
                className={classNames(
                    "h-full flex w-76 flex-col shadow-sidebar bg-surface-500 fixed inset-y-0 left-0 z-60 transition-transform duration-300 ease-out md:static md:z-auto md:w-full md:translate-x-0 md:gap-0.5 md:px-2.5 md:pt-6 md:pb-4",
                    {
                        "translate-x-0": isOpen,
                        "-translate-x-full": !isOpen,
                    }
                )}
            >
                <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-surface-520 pl-3 pr-1 md:hidden">
                    <BetterspaceWordmark
                        theme={theme}
                        textClassName="min-w-0 flex-1 text-[1.0625rem] text-text-500"
                    />
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("common:collapseSidebar")}
                        className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-text-300 hover:bg-surface-520"
                    >
                        <IconClose className="size-5" strokeWidth={1.33} />
                    </button>
                </div>
                <div className="hidden items-center gap-2.5 pt-1.5 pb-10 pl-2 md:flex">
                    <BetterspaceWordmark
                        theme={theme}
                        textClassName="text-text-500 text-[1.0625rem] leading-4.25"
                    />
                </div>
                <div className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto p-3 md:-mx-2.5 md:px-2.5 md:py-0">
                    {!isNavLoading &&
                        (companyId || locationId ? (
                            navItems.map(renderNavItem)
                        ) : (
                            <PermissionsGate>
                                {navItems.map(renderNavItem)}
                            </PermissionsGate>
                        ))}
                </div>
                <div className="mt-auto flex flex-col">
                    <div className="border-t border-surface-540 m-2" />
                    <div className="flex flex-col gap-1.25 px-3 pb-3 md:px-0 md:pb-0">
                        {isAdminPage && (
                            <SidebarActionButton
                                label={t("installation")}
                                icon={
                                    <IconInstallation
                                        className={NAV_ICON_CLASSES}
                                    />
                                }
                                isCompanyView={isCompanyView}
                                onClick={handleOnClickInstallation}
                            />
                        )}
                        <SidebarActionButton
                            label={t("help")}
                            icon={
                                <i
                                    aria-hidden
                                    className="pi pi-question-circle w-5.5 h-5.5 flex-none flex! items-center justify-center text-[1.375rem]!"
                                />
                            }
                            isCompanyView={isCompanyView}
                            onClick={handleOnClickHelp}
                        />
                    </div>
                    <div className="hidden border-t border-surface-540 mt-2 md:-mx-2.5 md:block" />
                    <div className="hidden text-center text-xxs text-text-250 leading-4 pt-3 pb-0.5 md:block">
                        {dayjs().year()} Betterspace GmbH
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
